import { useEffect, useState } from "react";
import { Plug, PlugZap, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { IDS } from "../../constants/testIds";
import { zelloStatus, zelloConnect, zelloDisconnect, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Field } from "./Field";

const PROVIDERS = [
  { value: "mock", label: "Mock simulator (built-in)" },
  { value: "zello_work", label: "Zello Work (ZelloWork network)" },
  { value: "zello_consumer", label: "Zello (consumer channels API)" },
  { value: "custom", label: "Other PTT service (custom WebSocket endpoint)" },
];

const STATUS_STYLE = {
  connected: "bg-emerald-50 text-emerald-700 border-emerald-200",
  connecting: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
  disconnected: "bg-slate-100 text-slate-600 border-slate-200",
};

export function ZelloTab({ form, set, onInput, isAdmin, onSaveFirst }) {
  const [status, setStatus] = useState({ status: "disconnected" });
  const [busy, setBusy] = useState(false);
  const isZello = form.provider === "zello_work" || form.provider === "zello_consumer";
  const canConnect = form.provider !== "mock";

  useEffect(() => {
    zelloStatus().then(setStatus).catch(() => {});
    const t = setInterval(() => zelloStatus().then(setStatus).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      await onSaveFirst();
      const s = await zelloConnect();
      setStatus(s);
      toast(s.status === "error" ? `Zello: ${s.error}` : "Connecting to Zello…");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { setStatus(await zelloDisconnect()); toast("Disconnected from Zello"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-3 pt-2">
      <div data-testid={IDS.zello.status} className={`flex items-center justify-between rounded-md border px-3 py-2 ${STATUS_STYLE[status.status] || STATUS_STYLE.disconnected}`}>
        <div className="flex items-center gap-2 text-sm font-medium">
          {status.status === "connecting" ? <Loader2 className="h-4 w-4 animate-spin" /> : status.status === "connected" ? <PlugZap className="h-4 w-4" /> : <Plug className="h-4 w-4" />}
          Live connector: <span className="font-mono uppercase text-xs">{status.status}</span>
        </div>
        {isAdmin && (status.status === "connected" || status.status === "connecting"
          ? <Button data-testid={IDS.zello.disconnect} size="sm" variant="outline" onClick={disconnect} disabled={busy}>Disconnect</Button>
          : <Button data-testid={IDS.zello.connect} size="sm" onClick={connect} disabled={busy || !canConnect}>Connect now</Button>)}
      </div>
      {status.error && <p className="text-[11px] text-red-600">{status.error}</p>}
      {status.status === "connected" && Object.keys(status.channels || {}).length > 0 && (
        <div className="text-[11px] text-slate-600 flex flex-wrap gap-1">
          {Object.entries(status.channels).map(([n, c]) => <span key={n} className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50">{n}: {c.status} · {c.users_online} online</span>)}
        </div>
      )}

      <Field label="Provider">
        <Select value={form.provider} onValueChange={set("provider")} disabled={!isAdmin}>
          <SelectTrigger data-testid={IDS.settings.provider}><SelectValue /></SelectTrigger>
          <SelectContent>{PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      {isZello && (
        <>
          {form.provider === "zello_work" && (
            <Field label="ZelloWork network name" hint="The part before .zellowork.com">
              <Input data-testid={IDS.settings.zelloNetwork} value={form.zello_network || ""} onChange={onInput("zello_network")} placeholder="mycompany" disabled={!isAdmin} />
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Zello username"><Input data-testid={IDS.settings.zelloUsername} value={form.zello_username || ""} onChange={onInput("zello_username")} disabled={!isAdmin} /></Field>
            <Field label="Password"><Input data-testid={IDS.settings.zelloPassword} type="password" value={form.zello_password || ""} onChange={onInput("zello_password")} disabled={!isAdmin} /></Field>
          </div>
          <Field label="Auth token (developer / channel API token)" hint={form.provider === "zello_consumer" ? "Required for consumer Zello — from developers.zello.com" : "Optional for ZelloWork"}>
            <Input data-testid={IDS.settings.authToken} type="password" value={form.auth_token || ""} onChange={onInput("auth_token")} disabled={!isAdmin} />
          </Field>
        </>
      )}
      {form.provider === "custom" && (
        <>
          <Field label="WebSocket endpoint">
            <Input data-testid={IDS.settings.apiEndpoint} value={form.api_endpoint || ""} onChange={onInput("api_endpoint")} placeholder="wss://ptt.example.com/ws" disabled={!isAdmin} />
          </Field>
          <Field label="Auth token">
            <Input data-testid={IDS.settings.authToken} type="password" value={form.auth_token || ""} onChange={onInput("auth_token")} disabled={!isAdmin} />
          </Field>
        </>
      )}
      {canConnect && (
        <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
          <div>
            <div className="text-sm font-medium">Auto-connect on startup</div>
            <div className="text-[11px] text-slate-500">Reconnect to Zello whenever the console backend starts.</div>
          </div>
          <Switch data-testid={IDS.zello.autoConnect} checked={!!form.zello_auto_connect} onCheckedChange={set("zello_auto_connect")} disabled={!isAdmin} />
        </div>
      )}
      <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
        When connected, the console joins your channels over the Zello Channel API and logs incoming traffic live. Until then the built-in simulator provides traffic.
        {!isAdmin && " Only administrators can change connector settings."}
      </p>
    </div>
  );
}
